import * as React from 'react'

import {
  Check,
  Copy,
  Ellipsis,
  FileDown,
  Link,
  RefreshCw,
  Star,
  Trash2,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import WorkflowStatusRows from '~/client/src/shared/components/WorkflowStatusSelector/WorkflowStatusRows'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import SitePermitAssignmentsStore from '~/client/src/shared/stores/domain/SitePermitAssignments.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { toast } from '~/client/src/shared/utils/toast'

import SitePermitCreationFormStore from '../WorkflowCreationForm.store'

// localization: translated

interface IProps {
  store: SitePermitCreationFormStore

  onStatusChange?(status: SitePermitStatus, stepId: string): void
  onOpenStatusSelector?(): void
  onReportSubmitClick(workflow: Workflow, callbackFn?: () => void): void
  onDuplicateClick(workflow: Workflow): void

  isDisabled?: boolean
  isMobileMode?: boolean

  sitePermitAssignmentsStore?: SitePermitAssignmentsStore
  state?: InitialState
}

// Gap and type match DeliveryWorkflowMoreMenu, the sibling ⋯ menu. The cursor
// overrides the menu primitive's default so the status list feels the same
// whichever control opened it — the caret's shell uses OptionsItem, which is
// pointer.
const ITEM_CLASS = 'tw:cursor-pointer tw:gap-2 tw:text-body-md'
const SUB_LINE_CLASS = 'tw:text-app-micro tw:text-muted-foreground'

@inject('sitePermitAssignmentsStore', 'state')
@observer
export default class WorkflowMoreMenu extends React.Component<IProps> {
  public render() {
    const { isDisabled, store, onStatusChange, isMobileMode } = this.props
    const { template, existingPermit, toggleDeleteConfirmModal } = store

    return (
      <DropdownMenu
        onOpenChange={this.resetSelectedField}
        onOpenChangeComplete={this.onMenuOpenChangeComplete}
      >
        <DropdownMenuTrigger
          disabled={isDisabled}
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={isDisabled}
              aria-label={Localization.translator.workflowMenu}
              className="tw:-mr-2 tw:text-foreground"
              data-testid="workflow-menu-button"
            />
          }
        >
          <Ellipsis aria-hidden="true" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="tw:w-[240px]">
          {!this.isDraft && !!existingPermit?.id && (
            <DropdownMenuItem
              className={ITEM_CLASS}
              onClick={this.copyWorkflowLink}
              data-testid="workflow-menu-copy-link-button"
            >
              <Link />
              {Localization.translator.copyLink}
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className={ITEM_CLASS}
            disabled={this.isDuplicateDisabled}
            onClick={this.duplicateWorkflow}
            data-testid="workflow-menu-duplicate-button"
          >
            <Copy />
            <span className="tw:flex tw:min-w-0 tw:flex-col">
              <span>{Localization.translator.duplicateCurrentWorkflow}</span>
              {this.isDuplicateDisabled && (
                <span className={SUB_LINE_CLASS}>
                  {Localization.translator.olderWorkflowCantBeDuplicate}
                </span>
              )}
            </span>
          </DropdownMenuItem>

          {!!template && !this.isDeleted && !this.isDraft && (
            <DropdownMenuItem
              className={ITEM_CLASS}
              onClick={this.toggleFollowing}
              data-testid="workflow-menu-subscribe-button"
            >
              <Star
                className={this.isAlreadyFollowed ? 'tw:fill-current' : null}
              />
              <span className="tw:flex tw:min-w-0 tw:flex-col">
                <span>
                  {this.isAlreadyFollowed
                    ? Localization.translator.unsubscribe
                    : Localization.translator.subscribe_verb}
                </span>
                <span className={SUB_LINE_CLASS}>
                  {this.isAlreadyFollowed
                    ? Localization.translator.followFormMenuDescr.toStop
                    : Localization.translator.followFormMenuDescr.toReceive}
                </span>
              </span>
            </DropdownMenuItem>
          )}

          {!!template &&
            this.isChangeStatusShown &&
            (isMobileMode ? (
              <DropdownMenuItem
                className={ITEM_CLASS}
                onClick={this.requestStatusSelector}
                data-testid="workflow-menu-update-status-button"
              >
                <RefreshCw />
                {Localization.translator.updateStatus}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className={ITEM_CLASS}
                  data-testid="workflow-menu-update-status-button"
                >
                  <RefreshCw />
                  {Localization.translator.updateStatus}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="tw:w-[280px]">
                  <WorkflowStatusRows
                    currentStatus={existingPermit.status}
                    currentWorkflowStepLevel={existingPermit.workflowStepLevel}
                    workflowSteps={template.stepsWithoutRecurring}
                    setStatusAndStep={onStatusChange}
                    renderRow={row => (
                      <DropdownMenuItem
                        key={row.key}
                        className={ITEM_CLASS}
                        onClick={row.onSelect}
                      >
                        {row.label}
                        {row.isSelected && (
                          <Check className="tw:ml-auto tw:size-4" />
                        )}
                      </DropdownMenuItem>
                    )}
                  />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))}

          {!!template && (
            <DropdownMenuItem
              className={ITEM_CLASS}
              onClick={this.submitReport}
              data-testid="workflow-menu-pdf-report-button"
            >
              <FileDown />
              {Localization.translator.emailPdfReport}
            </DropdownMenuItem>
          )}

          {!this.isDeleted && (
            <DropdownMenuItem
              className={ITEM_CLASS}
              variant="destructive"
              disabled={this.isDeleteDisabled}
              onClick={toggleDeleteConfirmModal}
              data-testid="workflow-menu-delete-button"
            >
              <Trash2 />
              <span className="tw:flex tw:min-w-0 tw:flex-col">
                <span>{Localization.translator.deleteWorkflow}</span>
                {this.isDeleteDisabled && (
                  <span className={SUB_LINE_CLASS}>
                    {this.deleteButtonText}
                  </span>
                )}
              </span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  private get isDeleted(): boolean {
    return this.props.store.existingPermit?.isDeleted
  }

  private get isDraft(): boolean {
    return this.props.store.existingPermit?.isDraft
  }

  private get isDeleteDisabled(): boolean {
    const { existingPermit, isFormEditAllowedForUser } = this.props.store
    return existingPermit?.isDone || !isFormEditAllowedForUser
  }

  private get deleteButtonText(): string {
    const { existingPermit, isFormEditAllowedForUser } = this.props.store
    if (existingPermit?.isDone) {
      return Localization.translator.workflowDeleteMenuDescr.isDone
    }
    if (!isFormEditAllowedForUser) {
      return Localization.translator.workflowDeleteMenuDescr.onlyFor
    }
  }

  private get isChangeStatusShown(): boolean {
    return (
      !!this.props.onStatusChange &&
      !this.isDeleted &&
      !this.isDraft &&
      this.props.store.canChangeWorkflowStatus
    )
  }

  private get isDuplicateDisabled(): boolean {
    return !this.props.store.isActualTypeVersion
  }

  private get isAlreadyFollowed(): boolean {
    const { store, sitePermitAssignmentsStore, state } = this.props
    const permitId = store?.existingPermit?.id
    if (!permitId) return false

    const map = sitePermitAssignmentsStore?.permitIdToAssignedUserIdsMap || {}
    return (map[permitId] || []).includes(state?.user?.id)
  }

  // base-ui keeps a modal backdrop and scroll lock alive through the menu's
  // exit animation, so the sheet can only open once the menu is fully closed.
  private shouldOpenStatusSelectorOnClose = false

  private requestStatusSelector = () => {
    this.shouldOpenStatusSelectorOnClose = true
  }

  private onMenuOpenChangeComplete = (isOpen: boolean) => {
    if (isOpen || !this.shouldOpenStatusSelectorOnClose) return

    this.shouldOpenStatusSelectorOnClose = false
    this.props.onOpenStatusSelector?.()
  }

  // The legacy shell dismissed an open field picker on every menu toggle; the
  // pickers still read that state, so both edges have to keep clearing it.
  private resetSelectedField = () => {
    this.props.store.resetSelectedField()
  }

  private toggleFollowing = () => {
    const { store, sitePermitAssignmentsStore, state } = this.props
    const permitId = store?.existingPermit?.id
    const userId = state?.user?.id
    if (!permitId) {
      return
    }

    if (this.isAlreadyFollowed) {
      sitePermitAssignmentsStore.removeUserFromAssignment(permitId, userId)
      return
    }

    sitePermitAssignmentsStore.addUserToAssignment(
      permitId,
      userId,
      state?.activeProject?.id,
    )
  }

  private copyWorkflowLink = () => {
    const { store, state } = this.props

    navigator.clipboard.writeText(
      Workflow.getDirectLinkToInstance(
        state?.activeProject?.code,
        store.existingPermit?.id,
      ),
    )
    toast.success(Localization.translator.copiedToClipboard)
  }

  private duplicateWorkflow = () => {
    this.props.onDuplicateClick(this.props.store.existingPermit)
  }

  private submitReport = () => {
    const { editablePermit, setIsReportDownloading, resetIsReportDownloading } =
      this.props.store

    setIsReportDownloading()
    this.props.onReportSubmitClick(editablePermit, resetIsReportDownloading)
  }
}
